import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Loader2, ChevronDown, ChevronUp, Users, BookOpen, AlertTriangle, Trophy, XCircle, FileSpreadsheet, FileText, Calendar, Filter, X } from 'lucide-react';
import { toast } from 'sonner';
import { ZohoCustomerLink } from '../../components/ZohoLinks';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function getYesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}
function getToday() {
  return new Date().toISOString().slice(0, 10);
}
function getCurrentMonth() {
  return new Date().toISOString().slice(0, 7);
}
function getMonthRange(month) {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

// ─── Searchable Dropdown ─────────────────────────────────────
function SearchableSelect({ options, value, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const selected = options.find(o => o.value === value);
  const filtered = query ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase())) : options;

  return (
    <div className="relative" data-testid={testId}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <Input
          ref={inputRef}
          placeholder={placeholder}
          value={open ? query : (selected ? selected.label : '')}
          onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          className="h-9 pl-8 text-xs w-full"
          data-testid={`${testId}-input`}
        />
        {value && (
          <button className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-red-400" onClick={(e) => { e.stopPropagation(); onChange(''); setQuery(''); }}>
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-400">No results</div>
            ) : filtered.map(o => (
              <div
                key={o.value}
                className={`px-3 py-2 text-xs cursor-pointer hover:bg-violet-50 ${o.value === value ? 'bg-violet-50 text-violet-700 font-medium' : 'text-slate-700'}`}
                onClick={() => { onChange(o.value); setQuery(''); setOpen(false); }}
                data-testid={`${testId}-option-${o.value}`}
              >
                {o.label}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Clickable Summary Card ──────────────────────────────────
function SummaryCard({ label, value, color, active, onClick, testId }) {
  return (
    <Card
      className={`border p-3 text-center cursor-pointer transition-all hover:shadow-sm ${active ? 'ring-2 ring-violet-400 border-violet-300' : 'hover:border-slate-300'}`}
      onClick={onClick}
      data-testid={testId}
    >
      <p className={`text-lg font-bold ${color}`}>{value ?? 0}</p>
      <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mt-0.5">{label}</p>
    </Card>
  );
}

const pctColor = (p) => p >= 80 ? 'text-emerald-600' : p >= 60 ? 'text-amber-600' : 'text-red-600';
const pctBg = (p) => p >= 80 ? 'bg-emerald-50' : p >= 60 ? 'bg-amber-50' : 'bg-red-50';

// ─── Student Row with expandable daily calendar ──────────────
function StudentRow({ student, dateFrom, dateTo, colSpan }) {
  const [expanded, setExpanded] = useState(false);

  const getDatesInRange = () => {
    const dates = [];
    const start = new Date(dateFrom);
    const end = new Date(dateTo);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().slice(0, 10));
    }
    return dates;
  };

  const s = student;
  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-violet-50/30"
        onClick={() => setExpanded(!expanded)}
        data-testid={`att-student-row-${s.enrollment_no}`}
      >
        <TableCell className="text-xs"><ZohoCustomerLink enrollmentNo={s.enrollment_no} zohoCustomerId={s.zoho_customer_id} /></TableCell>
        <TableCell className="text-sm font-medium">{s.name}</TableCell>
        <TableCell className="text-xs text-slate-500">{s.course}</TableCell>
        <TableCell className="text-xs"><span className="text-violet-600 font-medium">#{s.batch_number}</span> <span className="text-slate-400 text-[10px]">{s.batch_name}</span></TableCell>
        <TableCell className="text-xs text-slate-500">{s.trainer_name}</TableCell>
        <TableCell className="text-xs text-center font-medium">{s.total_classes}</TableCell>
        <TableCell className="text-xs text-center text-emerald-600 font-medium">{s.present}</TableCell>
        <TableCell className="text-xs text-center text-red-500 font-medium">{s.absent}</TableCell>
        <TableCell>
          <div className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold ${pctBg(s.attendance_pct)} ${pctColor(s.attendance_pct)}`}>
            {s.attendance_pct}%
          </div>
        </TableCell>
        <TableCell>{expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={colSpan} className="bg-slate-50/50 p-4">
            <p className="text-[10px] uppercase text-slate-400 font-semibold mb-2">Daily Attendance</p>
            <div className="flex flex-wrap gap-1">
              {getDatesInRange().map(d => {
                const day = new Date(d).getDate();
                const dayName = new Date(d).toLocaleDateString('en-IN', { weekday: 'short' });
                const isWknd = [0, 6].includes(new Date(d).getDay());
                const status = s.dates?.[d];
                let cls = 'w-8 h-8 rounded-sm flex flex-col items-center justify-center text-[9px] border ';
                if (status === 'present') cls += 'bg-emerald-100 border-emerald-300 text-emerald-700 font-bold';
                else if (status === 'absent') cls += 'bg-red-100 border-red-300 text-red-700 font-bold';
                else if (isWknd) cls += 'bg-slate-100 border-slate-200 text-slate-400';
                else cls += 'bg-white border-slate-200 text-slate-300';
                return (
                  <div key={d} className={cls} title={`${d} (${dayName}) — ${status || (isWknd ? 'Weekend' : 'No class')}`}>
                    <span className="leading-none">{day}</span>
                    <span className="leading-none text-[7px]">{status === 'present' ? 'P' : status === 'absent' ? 'A' : isWknd ? '-' : ''}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-2 text-[9px] text-slate-400">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-100 border border-emerald-300 rounded-sm" /> Present</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100 border border-red-300 rounded-sm" /> Absent</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-slate-100 border border-slate-200 rounded-sm" /> Weekend</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border border-slate-200 rounded-sm" /> No Class</span>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ─── Batch Drilldown (for trainer mode) ──────────────────────
function BatchDrilldown({ batch, dateFrom, dateTo }) {
  const [expanded, setExpanded] = useState(false);
  const b = batch;
  return (
    <Card className="border overflow-hidden" data-testid={`batch-drilldown-${b.batch_id}`}>
      <div
        className="flex items-center justify-between px-4 py-3 bg-slate-50/70 cursor-pointer hover:bg-slate-100/70"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="text-sm font-semibold text-slate-800">{b.batch_name || b.course_name}</div>
          <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">#{b.batch_number}</Badge>
          <span className="text-[10px] text-slate-400">{b.centre_name}</span>
          {b.timing && <span className="text-[10px] text-slate-400">{b.timing}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[9px]">{b.student_count} students</Badge>
          <Badge variant="outline" className="text-[9px]">{b.classes_taken} classes</Badge>
          <div className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold ${pctBg(b.avg_attendance_pct)} ${pctColor(b.avg_attendance_pct)}`}>
            {b.avg_attendance_pct}%
          </div>
          <Badge variant="outline" className={`text-[9px] ${b.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-700' : b.batch_status === 'Completed' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
            {b.batch_status}
          </Badge>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-[9px] uppercase">Enrollment</TableHead>
              <TableHead className="text-[9px] uppercase">Student</TableHead>
              <TableHead className="text-[9px] uppercase text-center">Classes</TableHead>
              <TableHead className="text-[9px] uppercase text-center">Present</TableHead>
              <TableHead className="text-[9px] uppercase text-center">Absent</TableHead>
              <TableHead className="text-[9px] uppercase text-center">Attendance %</TableHead>
              <TableHead className="w-6"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(b.students || []).map(s => (
                <StudentRow key={s.student_id} student={{...s, course: b.course_name, batch_number: b.batch_number, batch_name: b.batch_name, trainer_name: ''}} dateFrom={dateFrom} dateTo={dateTo} colSpan={7} />
              ))}
              {(!b.students || b.students.length === 0) && (
                <TableRow><TableCell colSpan={7} className="text-center text-xs text-slate-400 py-4">No students</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────
export default function AttendanceReportPage() {
  const [dateMode, setDateMode] = useState('yesterday');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [monthVal, setMonthVal] = useState(getCurrentMonth());
  const [trainerId, setTrainerId] = useState('');
  const [batchId, setBatchId] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardFilter, setCardFilter] = useState(null);

  const getDateRange = useCallback(() => {
    if (dateMode === 'yesterday') return { from: getYesterday(), to: getYesterday() };
    if (dateMode === 'today') return { from: getToday(), to: getToday() };
    if (dateMode === 'month') return getMonthRange(monthVal);
    if (dateMode === 'custom') return { from: customFrom || getYesterday(), to: customTo || getYesterday() };
    return { from: getYesterday(), to: getYesterday() };
  }, [dateMode, monthVal, customFrom, customTo]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const range = getDateRange();
    const params = new URLSearchParams({ date_from: range.from, date_to: range.to });
    if (trainerId) params.set('trainer_id', trainerId);
    if (batchId) params.set('batch_id', batchId);
    if (search) params.set('search', search);
    api.get(`/training/attendance-report?${params}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoading(false));
  }, [getDateRange, trainerId, batchId, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset batch when trainer changes
  useEffect(() => { setBatchId(''); }, [trainerId]);

  const summary = data?.summary || {};
  const trainers = (data?.trainers || []).map(t => ({ value: t.id, label: t.name }));

  // Batches filtered by trainer (if trainer selected, only show their batches)
  const batchOptions = (data?.batches || []).map(b => ({
    value: b.id,
    label: `#${b.batch_number} ${b.name}`,
  }));

  // Apply card filter to student list
  const filterStudents = (students) => {
    if (!cardFilter) return students;
    if (cardFilter === 'at_risk') return students.filter(s => s.attendance_pct > 0 && s.attendance_pct < 60);
    if (cardFilter === 'zero') return students.filter(s => s.attendance_pct === 0);
    if (cardFilter === 'perfect') return students.filter(s => s.attendance_pct === 100);
    return students;
  };

  const dateRange = getDateRange();

  const handleExport = () => {
    const rows = data?.mode === 'trainer'
      ? [...(data.running_batches || [])].flatMap(b => (b.students || []).map(s => ({ ...s, batch: `#${b.batch_number} ${b.batch_name}`, course: b.course_name })))
      : filterStudents(data?.students || []);
    const ws = XLSX.utils.json_to_sheet(rows.map(r => ({
      'Enrollment': r.enrollment_no, 'Student': r.name, 'Course': r.course || '',
      'Batch': r.batch || `#${r.batch_number} ${r.batch_name}`, 'Trainer': r.trainer_name || '',
      'Classes': r.total_classes, 'Present': r.present, 'Absent': r.absent,
      'Attendance %': r.attendance_pct,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf]), `attendance_report_${dateRange.from}_${dateRange.to}.xlsx`);
  };

  const handlePdfExport = () => {
    const rows = data?.mode === 'trainer'
      ? [...(data.running_batches || [])].flatMap(b => (b.students || []).map(s => ({ ...s, batch: `#${b.batch_number} ${b.batch_name}`, course: b.course_name })))
      : filterStudents(data?.students || []);

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Attendance Report', 14, 15);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`Period: ${dateRange.from} to ${dateRange.to}`, 14, 21);
    if (data?.trainer_name) doc.text(`Trainer: ${data.trainer_name}`, 14, 26);

    // Summary line
    const s = summary;
    const summaryY = data?.trainer_name ? 31 : 26;
    doc.setFontSize(8);
    doc.setTextColor(80);
    const summaryText = data?.mode === 'trainer'
      ? `Running: ${s.running_batches || 0} | Students: ${s.total_students || 0} | Avg: ${s.avg_attendance_pct || 0}% | At Risk: ${s.at_risk_count || 0} | 0%: ${s.zero_attendance_count || 0} | Perfect: ${s.perfect_attendance_count || 0}`
      : `Students: ${s.total_students || 0} | Classes: ${s.total_classes || 0} | Avg: ${s.avg_attendance_pct || 0}% | Present Rate: ${s.present_rate || 0}% | At Risk: ${s.at_risk_count || 0} | 0%: ${s.zero_attendance_count || 0} | Perfect: ${s.perfect_attendance_count || 0}`;
    doc.text(summaryText, 14, summaryY);

    // Table
    const headers = ['#', 'Enrollment', 'Student', 'Course', 'Batch', 'Trainer', 'Classes', 'Present', 'Absent', 'Att %'];
    const tableData = rows.map((r, i) => [
      i + 1,
      r.enrollment_no || '',
      r.name || '',
      r.course || '',
      r.batch || `#${r.batch_number || ''} ${r.batch_name || ''}`,
      r.trainer_name || '',
      r.total_classes,
      r.present,
      r.absent,
      `${r.attendance_pct}%`,
    ]);

    autoTable(doc, {
      startY: summaryY + 4,
      head: [headers],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [109, 40, 217], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [248, 247, 255] },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' },
        6: { halign: 'center' },
        7: { halign: 'center' },
        8: { halign: 'center' },
        9: { halign: 'center', fontStyle: 'bold' },
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.index === 9) {
          const pct = parseInt(hookData.cell.raw);
          if (pct >= 80) hookData.cell.styles.textColor = [5, 150, 105];
          else if (pct >= 60) hookData.cell.styles.textColor = [217, 119, 6];
          else hookData.cell.styles.textColor = [220, 38, 38];
        }
      },
      margin: { left: 14, right: 14 },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(160);
      doc.text(`Page ${i} of ${pageCount}`, pageW - 30, doc.internal.pageSize.getHeight() - 8);
      doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.getHeight() - 8);
    }

    doc.save(`attendance_report_${dateRange.from}_${dateRange.to}.pdf`);
  };

  const toggleCard = (key) => setCardFilter(prev => prev === key ? null : key);

  return (
    <div className="space-y-5" data-testid="attendance-report-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Attendance Report</h1>
        <p className="text-sm text-slate-400 mt-1">Student attendance analytics across batches and trainers</p>
      </div>

      {/* ─── Date Presets + Filters ──────────────────────────── */}
      <Card className="border p-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Date mode buttons */}
          <div className="flex items-center gap-1 border rounded-md overflow-hidden" data-testid="date-mode-selector">
            {['yesterday', 'today', 'month', 'custom'].map(m => (
              <button
                key={m}
                onClick={() => { setDateMode(m); setCardFilter(null); }}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${dateMode === m ? 'bg-violet-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                data-testid={`date-mode-${m}`}
              >
                {m === 'yesterday' ? 'Yesterday' : m === 'today' ? 'Today' : m === 'month' ? 'Month' : 'Custom'}
              </button>
            ))}
          </div>

          {dateMode === 'month' && (
            <Input type="month" value={monthVal} onChange={e => setMonthVal(e.target.value)} className="h-9 text-xs w-40" data-testid="month-picker" />
          )}
          {dateMode === 'custom' && (
            <>
              <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="h-9 text-xs w-36" data-testid="custom-from" />
              <span className="text-xs text-slate-400">to</span>
              <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="h-9 text-xs w-36" data-testid="custom-to" />
            </>
          )}

          <div className="w-52">
            <SearchableSelect
              options={trainers}
              value={trainerId}
              onChange={setTrainerId}
              placeholder="Search Trainer..."
              testId="trainer-filter"
            />
          </div>

          <div className="w-56">
            <SearchableSelect
              options={batchOptions}
              value={batchId}
              onChange={setBatchId}
              placeholder="Search Batch..."
              testId="batch-filter"
            />
          </div>

          <div className="flex-1 min-w-[180px]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300" />
              <Input
                placeholder="Search student, enrollment..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-9 text-xs pl-8"
                data-testid="search-input"
              />
            </div>
          </div>

          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={handleExport} disabled={!data} data-testid="export-excel-btn">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel
          </Button>
          <Button variant="outline" size="sm" className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={handlePdfExport} disabled={!data} data-testid="export-pdf-btn">
            <FileText className="w-3.5 h-3.5 mr-1" />PDF
          </Button>
        </div>

        {/* Date range indicator */}
        <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-400">
          <Calendar className="w-3 h-3" />
          <span>Showing: <span className="font-semibold text-slate-600">{dateRange.from}</span> to <span className="font-semibold text-slate-600">{dateRange.to}</span></span>
          {trainerId && data?.trainer_name && <Badge variant="outline" className="text-[9px]">Trainer: {data.trainer_name}</Badge>}
          {cardFilter && (
            <button className="ml-2 flex items-center gap-1 text-red-500 hover:text-red-600" onClick={() => setCardFilter(null)}>
              <X className="w-3 h-3" /> Clear card filter
            </button>
          )}
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
      ) : !data ? null : (
        <>
          {/* ─── Summary Cards ──────────────────────────────── */}
          {data.mode === 'students' ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="summary-cards">
              <SummaryCard label="Total Students" value={summary.total_students} color="text-slate-800" active={false} onClick={() => setCardFilter(null)} testId="card-total" />
              <SummaryCard label="Total Classes" value={summary.total_classes} color="text-violet-600" active={false} onClick={() => setCardFilter(null)} testId="card-classes" />
              <SummaryCard label="Avg Attendance" value={`${summary.avg_attendance_pct}%`} color={pctColor(summary.avg_attendance_pct)} active={false} onClick={() => setCardFilter(null)} testId="card-avg" />
              <SummaryCard label="Present Rate" value={`${summary.present_rate}%`} color="text-emerald-600" active={false} onClick={() => setCardFilter(null)} testId="card-present" />
              <SummaryCard label="At Risk (<60%)" value={summary.at_risk_count} color="text-amber-600" active={cardFilter === 'at_risk'} onClick={() => toggleCard('at_risk')} testId="card-at-risk" />
              <SummaryCard label="0% Attendance" value={summary.zero_attendance_count} color="text-red-600" active={cardFilter === 'zero'} onClick={() => toggleCard('zero')} testId="card-zero" />
              <SummaryCard label="Perfect (100%)" value={summary.perfect_attendance_count} color="text-emerald-600" active={cardFilter === 'perfect'} onClick={() => toggleCard('perfect')} testId="card-perfect" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="summary-cards">
              <SummaryCard label="Running Batches" value={summary.running_batches} color="text-emerald-600" active={false} onClick={() => setCardFilter(null)} testId="card-running" />
              <SummaryCard label="Completed" value={summary.completed_batches} color="text-blue-600" active={false} onClick={() => setCardFilter(null)} testId="card-completed" />
              <SummaryCard label="Total Students" value={summary.total_students} color="text-slate-800" active={false} onClick={() => setCardFilter(null)} testId="card-total" />
              <SummaryCard label="Avg Attendance" value={`${summary.avg_attendance_pct}%`} color={pctColor(summary.avg_attendance_pct)} active={false} onClick={() => setCardFilter(null)} testId="card-avg" />
              <SummaryCard label="At Risk (<60%)" value={summary.at_risk_count} color="text-amber-600" active={cardFilter === 'at_risk'} onClick={() => toggleCard('at_risk')} testId="card-at-risk" />
              <SummaryCard label="0% Attendance" value={summary.zero_attendance_count} color="text-red-600" active={cardFilter === 'zero'} onClick={() => toggleCard('zero')} testId="card-zero" />
              <SummaryCard label="Perfect (100%)" value={summary.perfect_attendance_count} color="text-emerald-600" active={cardFilter === 'perfect'} onClick={() => toggleCard('perfect')} testId="card-perfect" />
            </div>
          )}

          {/* ─── Content ────────────────────────────────────── */}
          {data.mode === 'students' ? (
            /* Individual Students View */
            <Card className="border p-0" data-testid="students-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-[10px] uppercase">Enrollment</TableHead>
                  <TableHead className="text-[10px] uppercase">Student</TableHead>
                  <TableHead className="text-[10px] uppercase">Course</TableHead>
                  <TableHead className="text-[10px] uppercase">Batch</TableHead>
                  <TableHead className="text-[10px] uppercase">Trainer</TableHead>
                  <TableHead className="text-[10px] uppercase text-center">Classes</TableHead>
                  <TableHead className="text-[10px] uppercase text-center">Present</TableHead>
                  <TableHead className="text-[10px] uppercase text-center">Absent</TableHead>
                  <TableHead className="text-[10px] uppercase">Attendance %</TableHead>
                  <TableHead className="w-6"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filterStudents(data.students || []).length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-12 text-xs text-slate-400">
                      {cardFilter ? 'No students match this filter' : 'No attendance data for the selected period'}
                    </TableCell></TableRow>
                  ) : filterStudents(data.students || []).map(s => (
                    <StudentRow key={s.student_id} student={s} dateFrom={dateRange.from} dateTo={dateRange.to} colSpan={10} />
                  ))}
                </TableBody>
              </Table>
            </Card>
          ) : (
            /* Trainer → Batch Drilldown View */
            <div className="space-y-5">
              {(data.running_batches || []).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />Running Batches ({data.running_batches.length})
                  </h3>
                  <div className="space-y-2">
                    {data.running_batches.map(b => (
                      <BatchDrilldown key={b.batch_id} batch={b} dateFrom={dateRange.from} dateTo={dateRange.to} />
                    ))}
                  </div>
                </div>
              )}
              {(data.completed_batches || []).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />Completed Batches ({data.completed_batches.length})
                  </h3>
                  <div className="space-y-2">
                    {data.completed_batches.map(b => (
                      <BatchDrilldown key={b.batch_id} batch={b} dateFrom={dateRange.from} dateTo={dateRange.to} />
                    ))}
                  </div>
                </div>
              )}
              {(data.running_batches || []).length === 0 && (data.completed_batches || []).length === 0 && (
                <Card className="border p-12 text-center">
                  <BookOpen className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                  <p className="text-xs text-slate-400">No batches with attendance data for this trainer in the selected period</p>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
